/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { FC } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useSWRInfinite from 'swr/infinite';

import { ImageRenderer } from '@components/media/renderers/image';
import { ICollection } from '@interface/collection';
import { collectionsFetcher } from '@utils/fetcher';
import { getURLFromIPFSorHTTP } from '@utils/ipfs';
import { useMedia } from '@utils/use-media';
import { Box, Button, Container, Step, Text } from '@versumstudios/ui';

const Grid = dynamic(() => import('@components/grid'), { ssr: false });

interface ISmartGridCollections {
  api: string;
  query?: string;
  limit: number;
}

// same as SmartGridToken but without contextual routing
export const SmartGridCollections: FC<ISmartGridCollections> = ({ api, query, limit }) => {
  const router = useRouter();
  const columns = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], [5, 3], 2);
  const space = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], ['32px', '16px'], '0px');
  const { data, error, size, setSize } = useSWRInfinite(
    (i: number, previousPageData: any) => {
      if (previousPageData && !previousPageData.length) return null;
      return `${api}${query ? `?${query}&offset=${(i + 0) * limit}` : `?offset=${(i + 0) * limit}`}`;
    },
    collectionsFetcher,
    {
      revalidateIfStale: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    }
  );

  const items = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data && data.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < limit);

  const renderThumbnail = (collection: ICollection) => {
    return (
      <div key={collection.id} style={{ overflow: 'hidden', position: 'relative' }}>
        <Link
          key={collection.id}
          href={`/collections/${collection.name}/collection`}
          locale={router.locale}
          prefetch={false}
          passHref
        >
          <a>
            <ImageRenderer
              src={getURLFromIPFSorHTTP(collection.logo_uri)}
              width={512}
              height={512}
              alt={collection.screen_name}
              inDetailView={false}
            />
          </a>
        </Link>

        <Box css={{ justifyContent: 'space-between', padding: '$space$4' }}>
          <Box css={{ flexDirection: 'column' }}>
            <Text unset ellipsis css={{ fontWeight: 700, overflow: 'hidden' }}>
              {collection.screen_name}
            </Text>
          </Box>
        </Box>
      </div>
    );
  };

  // TODO: remove filtering from hicetnunc
  return (
    <>
      {!isLoadingInitialData && items.length === 0 && (
        <Container>
          <Text unset css={{ color: '$secondaryFill' }}>
            No data to display
          </Text>
        </Container>
      )}

      <Grid columns={columns} space={space}>
        {items
          .filter((collection: any) => collection.name !== 'hicetnunc')
          .map((collection: ICollection) => renderThumbnail(collection))}
      </Grid>

      {!isReachingEnd && (
        <Box css={{ padding: '$1 0' }} horizontal>
          {isLoadingMore ? (
            <Step progress />
          ) : (
            <Button onClick={() => setSize(size + 1)} variant="secondary">
              Load more
            </Button>
          )}
        </Box>
      )}
    </>
  );
};
